import { message } from 'antd'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components/macro'
import {
  HARBOUR_COPY_EMAILS_ACTION,
  HARBOUR_COPY_EMAILS_TOAST,
  HARBOUR_COPY_LINK_ACTION,
  HARBOUR_COPY_WHATSAPP_ACTION,
  HARBOUR_CLOSED_LOG_COPY,
  HARBOUR_LOG_FOLLOW_UP_CLOSED_COPY,
  HARBOUR_LOG_FOLLOW_UP_WAIT_COPY,
  HARBOUR_INTENTS,
  HARBOUR_LAST_LOG_TITLE,
  HARBOUR_LOG_SAVE_LABEL,
  HARBOUR_LOG_START_WORK_FIRST,
  HARBOUR_NO_PHONE_COPY,
  HARBOUR_OLLIE_SUGGEST_ACTION,
  HARBOUR_OLLIE_SUGGEST_BUSY,
  HARBOUR_OLLIE_SUGGEST_COPY_EMAIL,
  HARBOUR_OLLIE_SUGGEST_CAPTION,
  HARBOUR_OLLIE_SUGGEST_COPY_LOG,
  HARBOUR_OLLIE_SUGGEST_COPY_WHATSAPP,
  HARBOUR_OLLIE_SUGGEST_TOAST,
  HARBOUR_OUTCOME,
  HARBOUR_OUTCOMES,
  HARBOUR_PARKED_SAME_CARD_COPY,
  HARBOUR_SEARCH_MISS_ACTION,
  HARBOUR_WHATSAPP_CHAT_ACTION,
  canHarbourReassign,
  getHarbourSpartacusHandoffQuery,
  getHarbourIntentLabel,
  getHarbourOutcomeLabel,
  isHarbourParkedColumn,
} from 'src/constants/harbour'
import {
  logHarbourAction,
  startHarbourWork,
  suggestHarbourOllieNextStep,
} from 'src/utils/harbourApi'
import {
  getHarbourCardChefName,
  getHarbourCardCsOwnerLabel,
  getHarbourCardPlanLabel,
  shouldShowHarbourStartWork,
} from 'src/utils/harbourCard'
import {
  getHarbourCardLadder,
  getHarbourCardRadar,
  splitHarbourCardLadder,
} from 'src/utils/harbourLastUsed'
import {
  buildHarbourLogBody,
  canSaveHarbourLog,
  getHarbourDefaultFollowUpDate,
  shouldShowHarbourLogFollowUpDate,
  getHarbourLastLogMemory,
} from 'src/utils/harbourLog'
import {
  formatHarbourDate,
  getHarbourSisterGroupOtherSites,
} from 'src/utils/harbourMappers'
import {
  copyHarbourEmails,
  getHarbourVenueEmails,
} from 'src/utils/harbourEmails'
import {
  copyHarbourPhone,
  copyHarbourText,
  getHarbourPhoneHref,
  getHarbourPhoneNumber,
  getHarbourWhatsAppChatHref,
} from 'src/utils/harbourPhone'
import {
  toastHarbourAlreadyYours,
  toastHarbourLogged,
  toastHarbourMutationError,
  toastHarbourStartWorkSuccess,
} from 'src/utils/harbourToast'
import { HARBOUR_TOOLTIPS } from 'src/utils/harbourTooltips'
import { buildHarbourVenueShareUrl } from 'src/utils/harbourShare'
import OllieIcon from 'src/components/icons/OllieIcon'
import HarbourDrawerOfframps from './HarbourDrawerOfframps'
import HarbourDrawerNotes from './HarbourDrawerNotes'
import { useHarbourDrawer } from './useHarbourDrawer'

/**
 * Name and phone from the card/search hit are fine on first paint.
 * Plan, why-here, users, and last-used wait for GET — seed.paid is often
 * missing, so painting it would flash Free then Growth.
 */
export function getHarbourDrawerShell(seed = {}) {
  return {
    venueId: seed.venueId,
    venueName: seed.venueName || '',
    phoneNumber: seed.phoneNumber || seed.contact?.phone || '',
    contact: seed.contact || null,
    sisterGroup: seed.sisterGroup || null,
  }
}

/**
 * Venue briefing + hard-to-skip log. Opens as a centred overlay (Catch
 * Board muscle memory) so a card click is obvious. Fetches GET /venues/:id
 * on open. Reassign stays hidden: the API has no lead / Kev gate.
 */
const HarbourDrawer = ({
  seed = {},
  onClose,
  onOpenSpartacus,
  onWorkChanged,
  onSelectVenue,
} = {}) => {
  const venueId = seed.venueId
  const { drawer, loading, error, refresh } = useHarbourDrawer({
    venueId,
    enabled: Boolean(venueId),
  })
  const briefingReady = Boolean(drawer?.venueId === venueId)
  const briefing = briefingReady ? drawer : getHarbourDrawerShell(seed)
  const sisterGroup = briefingReady
    ? drawer.sisterGroup || seed.sisterGroup || null
    : seed.sisterGroup || briefing.sisterGroup || null
  const otherSites = getHarbourSisterGroupOtherSites(sisterGroup, venueId)
  const startWork = drawer?.startWork || {}
  const alreadyOwned = Boolean(startWork.alreadyOwned)
  const alreadyTaken = Boolean(startWork.alreadyTaken)
  const parked = isHarbourParkedColumn(briefing.column)
  const [outcome, setOutcome] = useState('')
  const [intent, setIntent] = useState('')
  const [followUpDate, setFollowUpDate] = useState('')
  const [note, setNote] = useState('')
  const [startBusy, setStartBusy] = useState(false)
  const [saveBusy, setSaveBusy] = useState(false)
  const [savedLog, setSavedLog] = useState(null)
  const [suggestion, setSuggestion] = useState(null)
  const [suggestBusy, setSuggestBusy] = useState(false)

  useEffect(() => {
    setOutcome('')
    setIntent('')
    setFollowUpDate('')
    setNote('')
    setSavedLog(null)
    setSuggestion(null)
    setSuggestBusy(false)
  }, [venueId])

  const handleOutcomeChange = nextOutcome => {
    setOutcome(nextOutcome)
    setFollowUpDate(getHarbourDefaultFollowUpDate(nextOutcome))
  }

  const phone = getHarbourPhoneNumber(briefing)
  const phoneHref = getHarbourPhoneHref(phone)
  const whatsAppChatHref = getHarbourWhatsAppChatHref(phone)
  const emails = getHarbourVenueEmails(briefing)
  const chefName = getHarbourCardChefName(briefing)
  const planLabel = briefingReady ? getHarbourCardPlanLabel(briefing) : ''
  const csOwnerLabel = briefingReady
    ? getHarbourCardCsOwnerLabel(briefing)
    : null
  const radar = getHarbourCardRadar(briefing)
  const ladder = getHarbourCardLadder(briefing)
  const { critical: ladderCritical, secondary: ladderSecondary } =
    splitHarbourCardLadder(ladder)
  const whatsAppText = briefing.whatsAppText || briefing.whatsAppOpener || ''
  const canSave =
    canSaveHarbourLog({ outcome, alreadyOwned, column: briefing.column }) &&
    !saveBusy
  const showFollowUpDate = shouldShowHarbourLogFollowUpDate(outcome)
  const showStartWork = shouldShowHarbourStartWork({
    canStartWork: startWork.canStartWork,
    alreadyOwned,
    alreadyTaken,
    column: briefing.column,
  })
  const lastLog =
    (briefingReady ? getHarbourLastLogMemory(briefing) : null) || savedLog

  const handleCopyPhone = () => {
    async function copyPhone() {
      try {
        await copyHarbourPhone(phone)
        message.success('Phone copied')
      } catch (err) {
        message.error(err.message || 'Could not copy phone')
      }
    }
    copyPhone()
  }

  const handleCopyEmails = () => {
    async function copyEmails() {
      try {
        await copyHarbourEmails(briefing)
        message.success(HARBOUR_COPY_EMAILS_TOAST)
      } catch (err) {
        message.error(err.message || 'Could not copy emails')
      }
    }
    copyEmails()
  }

  const handleCopyWhatsApp = () => {
    async function copyWhatsApp() {
      try {
        await copyHarbourText(whatsAppText, 'No WhatsApp opener')
        message.success('WhatsApp opener copied')
      } catch (err) {
        message.error(err.message || 'Could not copy WhatsApp opener')
      }
    }
    copyWhatsApp()
  }

  const handleCopyLink = () => {
    async function copyLink() {
      try {
        await copyHarbourText(
          buildHarbourVenueShareUrl(venueId),
          'No link to copy',
        )
        message.success('Link copied')
      } catch (err) {
        message.error(err.message || 'Could not copy link')
      }
    }
    copyLink()
  }

  const handleSuggestNextStep = () => {
    async function suggest() {
      if (!venueId || suggestBusy) {
        return
      }
      setSuggestBusy(true)
      try {
        const result = await suggestHarbourOllieNextStep({
          venueId,
          whyHere: briefing.whyHere,
          suggestedNextStep: briefing.suggestedNextStep,
          talkingPoints: briefing.talkingPoints,
          contactName: chefName,
          plan: planLabel,
        })
        setSuggestion(result)
      } catch (err) {
        toastHarbourMutationError(err, { venueName: briefing.venueName })
      } finally {
        setSuggestBusy(false)
      }
    }
    suggest()
  }

  const handleCopyOllieWhatsApp = () => {
    async function copyWhatsApp() {
      try {
        await copyHarbourText(suggestion?.whatsAppText, 'No WhatsApp copy')
        message.success(HARBOUR_OLLIE_SUGGEST_TOAST)
      } catch (err) {
        message.error(err.message || 'Could not copy WhatsApp')
      }
    }
    copyWhatsApp()
  }

  const handleCopyOllieLog = () => {
    async function copyLog() {
      try {
        const value = await copyHarbourText(suggestion?.logNote, 'No log copy')
        if (!String(note || '').trim()) {
          setNote(value)
        }
        message.success(HARBOUR_OLLIE_SUGGEST_TOAST)
      } catch (err) {
        message.error(err.message || 'Could not copy log')
      }
    }
    copyLog()
  }

  const handleCopyOllieEmail = () => {
    async function copyEmail() {
      try {
        await copyHarbourText(suggestion?.emailText, 'No email copy')
        message.success(HARBOUR_OLLIE_SUGGEST_TOAST)
      } catch (err) {
        message.error(err.message || 'Could not copy email')
      }
    }
    copyEmail()
  }

  const handleStartWork = () => {
    if (!venueId || startBusy) {
      return
    }
    if (alreadyOwned && !parked) {
      toastHarbourAlreadyYours(briefing.venueName)
      return
    }
    if (alreadyTaken) {
      toastHarbourMutationError(
        {
          alreadyTaken: true,
          ownerName: startWork.ownerName || briefing.ownerName,
          venueName: briefing.venueName,
          message: startWork.takenMessage,
        },
        { venueName: briefing.venueName },
      )
      return
    }

    async function startWorkOnVenue() {
      setStartBusy(true)
      try {
        const result = await startHarbourWork({ venueId })
        toastHarbourStartWorkSuccess(result, briefing.venueName)
        onWorkChanged?.()
        refresh()
      } catch (err) {
        toastHarbourMutationError(err, { venueName: briefing.venueName })
      } finally {
        setStartBusy(false)
      }
    }

    startWorkOnVenue()
  }

  const handleSaveLog = () => {
    if (!canSave || !venueId) {
      return
    }

    async function saveLog() {
      setSaveBusy(true)
      try {
        const result = await logHarbourAction(
          buildHarbourLogBody({
            venueId,
            outcome,
            intent,
            note,
            followUpDate,
          }),
        )
        const memory = getHarbourLastLogMemory({
          lastOutcome: outcome,
          lastIntent: intent,
          lastNote: note,
          nextFollowUpAt: result.nextFollowUpAt,
          followUpDate,
        })
        toastHarbourLogged(memory)
        setSavedLog(memory)
        setOutcome('')
        setIntent('')
        setFollowUpDate('')
        setNote('')
        onWorkChanged?.()
        refresh()
      } catch (err) {
        toastHarbourMutationError(err, { venueName: briefing.venueName })
      } finally {
        setSaveBusy(false)
      }
    }

    saveLog()
  }

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        onClose?.()
      }
    }
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose])

  return (
    <Overlay
      data-testid='harbour-drawer-overlay'
      data-placement='center'
      onClick={event => {
        if (event.target === event.currentTarget) {
          onClose?.()
        }
      }}
      role='presentation'
    >
      <Panel
        data-testid='harbour-drawer'
        data-venue-id={venueId || ''}
        data-loading={loading && !briefingReady ? 'true' : 'false'}
        data-overdue={briefing.overdue ? 'true' : 'false'}
        data-forgotten={briefing.forgotten ? 'true' : 'false'}
        data-urgency={
          briefing.overdue ? 'overdue' : briefing.forgotten ? 'forgotten' : ''
        }
        role='dialog'
        aria-modal='true'
        aria-labelledby='harbour-drawer-title'
        onClick={event => event.stopPropagation()}
      >
        <Head>
          <TitleBlock>
            <Title id='harbour-drawer-title'>
              {briefing.venueName || 'Venue'}
            </Title>
            <UrgencyRow>
              {briefing.forgotten ? (
                <UrgentBadge
                  data-testid='harbour-drawer-forgotten'
                  title={HARBOUR_TOOLTIPS.forgotten}
                >
                  Forgotten
                </UrgentBadge>
              ) : null}
              {briefing.overdue ? (
                <UrgentBadge
                  data-follow-up='overdue'
                  data-testid='harbour-drawer-follow-up'
                  title={HARBOUR_TOOLTIPS.overdue}
                >
                  {briefing.nextFollowUpLabel || 'Overdue'}
                </UrgentBadge>
              ) : null}
            </UrgencyRow>
          </TitleBlock>
          <HeadAside>
            {briefingReady && planLabel ? (
              <PlanBadge
                data-plan={briefing.paid ? 'paid' : 'free'}
                data-testid='harbour-drawer-plan'
              >
                {planLabel}
              </PlanBadge>
            ) : null}
            <Close
              type='button'
              onClick={onClose}
              data-testid='harbour-drawer-close'
            >
              Close
            </Close>
          </HeadAside>
        </Head>

        {phone || emails.length > 0 || chefName ? (
          <ContactBlock
            data-testid='harbour-drawer-contact'
            data-no-phone={phone ? 'false' : 'true'}
          >
            {phone ? (
              <ContactRow>
                <ContactName data-testid='harbour-drawer-chef'>
                  {chefName || 'No named contact'}
                </ContactName>
                <ContactSep aria-hidden='true'>·</ContactSep>
                {phoneHref ? (
                  <PhoneLink
                    href={phoneHref}
                    title={HARBOUR_TOOLTIPS.call}
                    data-testid='harbour-drawer-call'
                  >
                    {phone}
                  </PhoneLink>
                ) : (
                  <PhoneNumber>{phone}</PhoneNumber>
                )}
                <GhostButton
                  type='button'
                  title={HARBOUR_TOOLTIPS.copyPhone}
                  onClick={handleCopyPhone}
                  data-testid='harbour-drawer-copy-phone'
                >
                  Copy
                </GhostButton>
                {whatsAppChatHref ? (
                  <WhatsAppChatLink
                    href={whatsAppChatHref}
                    title={HARBOUR_TOOLTIPS.whatsAppChat}
                    data-testid='harbour-drawer-whatsapp-chat'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {HARBOUR_WHATSAPP_CHAT_ACTION}
                  </WhatsAppChatLink>
                ) : null}
              </ContactRow>
            ) : (
              <>
                <ContactRow>
                  <ContactName data-testid='harbour-drawer-chef'>
                    {chefName || 'No named contact'}
                  </ContactName>
                </ContactRow>
                <NoPhoneRow data-testid='harbour-drawer-no-phone'>
                  {HARBOUR_NO_PHONE_COPY} — skip this call
                </NoPhoneRow>
              </>
            )}
            {emails.length > 0 ? (
              <EmailRow data-emphasized={!phone ? 'true' : 'false'}>
                <EmailList data-testid='harbour-drawer-emails'>
                  {emails.join(' · ')}
                </EmailList>
                <GhostButton
                  type='button'
                  title={HARBOUR_TOOLTIPS.copyEmails}
                  data-testid='harbour-drawer-copy-emails'
                  onClick={handleCopyEmails}
                >
                  {HARBOUR_COPY_EMAILS_ACTION}
                </GhostButton>
              </EmailRow>
            ) : null}
            {csOwnerLabel ? (
              <CsOwner
                title={HARBOUR_TOOLTIPS.csOwner}
                data-testid='harbour-drawer-owner'
              >
                {csOwnerLabel}
              </CsOwner>
            ) : null}
          </ContactBlock>
        ) : null}

        {loading && !briefingReady ? <Status>Loading briefing…</Status> : null}
        {error ? (
          <Status role='alert'>
            {error}{' '}
            <GhostButton
              type='button'
              onClick={refresh}
              data-testid='harbour-drawer-retry'
            >
              Retry
            </GhostButton>
          </Status>
        ) : null}

        {briefingReady && briefing.whyHere ? (
          <WhyHere data-testid='harbour-drawer-why'>{briefing.whyHere}</WhyHere>
        ) : null}
        {briefingReady && briefing.suggestedNextStep ? (
          <Suggested data-testid='harbour-drawer-step'>
            {briefing.suggestedNextStep}
          </Suggested>
        ) : null}

        {briefingReady && briefing.talkingPoints?.length > 0 ? (
          <Block
            data-testid='harbour-talking-points'
            title={HARBOUR_TOOLTIPS.talkingPoints}
          >
            <BlockLabel>Talking points</BlockLabel>
            <List>
              {briefing.talkingPoints.map(point => (
                <li key={point}>{point}</li>
              ))}
            </List>
          </Block>
        ) : null}

        {briefingReady && otherSites.length > 0 ? (
          <Block data-testid='harbour-drawer-sisters'>
            <BlockLabel>Other sites in this group</BlockLabel>
            <List>
              {otherSites.map(site => (
                <li key={site.venueId}>
                  <GhostButton
                    type='button'
                    data-testid={`harbour-drawer-sister-${site.venueId}`}
                    onClick={event => {
                      event.stopPropagation()
                      onSelectVenue?.({
                        venueId: site.venueId,
                        venueName: site.venueName,
                      })
                    }}
                  >
                    {site.venueName || site.venueId}
                  </GhostButton>
                </li>
              ))}
            </List>
          </Block>
        ) : null}

        {briefingReady ? (
          <Block
            data-testid='harbour-ollie-suggest'
            data-variant='ollie'
            title={HARBOUR_TOOLTIPS.suggestOllie}
          >
            <BlockLabelWithIcon>
              <OllieIcon size={16} />
              <BlockLabel>Ollie</BlockLabel>
            </BlockLabelWithIcon>
            <SecondaryButton
              type='button'
              title={HARBOUR_TOOLTIPS.suggestOllie}
              disabled={suggestBusy}
              data-testid='harbour-suggest-next-step'
              onClick={handleSuggestNextStep}
            >
              {suggestBusy
                ? HARBOUR_OLLIE_SUGGEST_BUSY
                : HARBOUR_OLLIE_SUGGEST_ACTION}
            </SecondaryButton>
            {suggestion?.nextStep ? (
              <Suggested data-testid='harbour-ollie-next'>
                {suggestion.nextStep}
              </Suggested>
            ) : null}
            {suggestion ? (
              <>
                <Caption data-testid='harbour-ollie-suggest-caption'>
                  {HARBOUR_OLLIE_SUGGEST_CAPTION}
                </Caption>
                <Actions>
                  <SecondaryButton
                    type='button'
                    title={HARBOUR_TOOLTIPS.suggestOllie}
                    data-testid='harbour-ollie-copy-whatsapp'
                    onClick={handleCopyOllieWhatsApp}
                  >
                    {HARBOUR_OLLIE_SUGGEST_COPY_WHATSAPP}
                  </SecondaryButton>
                  <SecondaryButton
                    type='button'
                    title={HARBOUR_TOOLTIPS.suggestOllie}
                    data-testid='harbour-ollie-copy-email'
                    onClick={handleCopyOllieEmail}
                  >
                    {HARBOUR_OLLIE_SUGGEST_COPY_EMAIL}
                  </SecondaryButton>
                  <SecondaryButton
                    type='button'
                    title={HARBOUR_TOOLTIPS.suggestOllie}
                    data-testid='harbour-ollie-copy-log'
                    onClick={handleCopyOllieLog}
                  >
                    {HARBOUR_OLLIE_SUGGEST_COPY_LOG}
                  </SecondaryButton>
                </Actions>
              </>
            ) : null}
          </Block>
        ) : null}

        {briefingReady && radar.length > 0 ? (
          <Block data-testid='harbour-drawer-radar'>
            <BlockLabel>Last used</BlockLabel>
            <ChipRow>
              {radar.map(chip => (
                <Chip
                  key={chip.key}
                  data-tone={chip.tone}
                  data-testid={`harbour-drawer-radar-${chip.key}`}
                >
                  {chip.label}: {chip.text}
                </Chip>
              ))}
            </ChipRow>
            {ladderCritical.length > 0 || ladderSecondary.length > 0 ? (
              <ChipRow data-testid='harbour-drawer-ladder'>
                {ladderCritical.map(tick => (
                  <Chip
                    key={tick.key}
                    data-tier={tick.tier}
                    data-tone={tick.tone}
                    data-testid={`harbour-drawer-ladder-${tick.key}`}
                  >
                    {tick.label}: {tick.text}
                  </Chip>
                ))}
                {ladderSecondary.length > 0 ? (
                  <ChipRowSecondary data-testid='harbour-drawer-ladder-secondary'>
                    {ladderSecondary.map(tick => (
                      <Chip
                        key={tick.key}
                        data-tier={tick.tier}
                        data-tone={tick.tone}
                        data-testid={`harbour-drawer-ladder-${tick.key}`}
                      >
                        {tick.label}: {tick.text}
                      </Chip>
                    ))}
                  </ChipRowSecondary>
                ) : null}
              </ChipRow>
            ) : null}
          </Block>
        ) : null}

        {briefingReady &&
        (briefing.users?.length > 0 || briefing.singleUserRisk?.caption) ? (
          <Block data-testid='harbour-drawer-users'>
            <BlockLabel>Users</BlockLabel>
            {briefing.singleUserRisk?.caption ? (
              <Caption>{briefing.singleUserRisk.caption}</Caption>
            ) : null}
            <List>
              {(briefing.users || []).map(user => {
                const name = String(user.name || '').trim()
                const email = String(user.email || '').trim()
                return (
                  <li key={user.accountId || user.email || user.name}>
                    {name || email || 'User'}
                    {user.isOwner ? ' · owner' : ' · extra'}
                    {name && email ? ` · ${email}` : ''}
                    {user.lastSeenAt
                      ? ` · last seen ${formatHarbourDate(user.lastSeenAt)}`
                      : ' · never seen'}
                  </li>
                )
              })}
            </List>
          </Block>
        ) : null}

        {alreadyTaken ? (
          <Status role='alert' data-testid='harbour-drawer-taken'>
            {startWork.takenMessage || 'Someone else is already on this venue.'}
          </Status>
        ) : null}

        <Actions>
          {onOpenSpartacus ? (
            <SecondaryButton
              type='button'
              title={HARBOUR_TOOLTIPS.openSpartacus}
              data-testid='harbour-drawer-open-spartacus'
              onClick={() =>
                onOpenSpartacus?.(getHarbourSpartacusHandoffQuery(briefing))
              }
            >
              {HARBOUR_SEARCH_MISS_ACTION}
            </SecondaryButton>
          ) : null}
          {venueId ? (
            <SecondaryButton
              type='button'
              title={HARBOUR_TOOLTIPS.copyLink}
              data-testid='harbour-copy-link'
              onClick={handleCopyLink}
            >
              {HARBOUR_COPY_LINK_ACTION}
            </SecondaryButton>
          ) : null}
          {whatsAppText ? (
            <SecondaryButton
              type='button'
              title={HARBOUR_TOOLTIPS.whatsApp}
              data-testid='harbour-copy-whatsapp'
              onClick={handleCopyWhatsApp}
            >
              {HARBOUR_COPY_WHATSAPP_ACTION}
            </SecondaryButton>
          ) : null}
          {showStartWork ? (
            <PrimaryButton
              type='button'
              title={HARBOUR_TOOLTIPS.startWork}
              disabled={startBusy}
              data-testid='harbour-drawer-start-work'
              onClick={handleStartWork}
            >
              {startBusy ? 'Starting…' : 'Start work'}
            </PrimaryButton>
          ) : null}
          {alreadyOwned && !parked ? (
            <Caption>You&apos;re on this venue.</Caption>
          ) : null}
          {parked && !alreadyTaken ? (
            <Caption data-testid='harbour-parked-same-card'>
              {HARBOUR_PARKED_SAME_CARD_COPY}
            </Caption>
          ) : null}
          {canHarbourReassign() ? (
            <SecondaryButton type='button' data-testid='harbour-reassign'>
              Reassign
            </SecondaryButton>
          ) : null}
        </Actions>

        <HarbourDrawerNotes
          drawer={briefing}
          visible={briefingReady}
          venueId={venueId}
        />

        <Log data-testid='harbour-log'>
          <BlockLabel>Log</BlockLabel>
          {lastLog ? (
            <LastLog data-testid='harbour-last-log'>
              <BlockLabel>{HARBOUR_LAST_LOG_TITLE}</BlockLabel>
              <LastLogLine data-testid='harbour-last-log-outcome'>
                {lastLog.outcomeLabel}
                {lastLog.intentLabel ? ` · ${lastLog.intentLabel}` : ''}
              </LastLogLine>
              {lastLog.followUpCaption ? (
                <Caption data-testid='harbour-last-log-follow-up'>
                  {lastLog.followUpCaption}
                </Caption>
              ) : null}
              {lastLog.note ? (
                <LastLogNote data-testid='harbour-last-log-note'>
                  {lastLog.note}
                </LastLogNote>
              ) : null}
              {lastLog.outcome === HARBOUR_OUTCOME.CLOSED ? (
                <Caption data-testid='harbour-last-log-recovered-hint'>
                  {HARBOUR_CLOSED_LOG_COPY}
                </Caption>
              ) : null}
            </LastLog>
          ) : null}
          {parked && !alreadyTaken ? (
            <Caption data-testid='harbour-log-blocked'>
              {HARBOUR_PARKED_SAME_CARD_COPY}
            </Caption>
          ) : null}
          {!alreadyOwned && !alreadyTaken && !parked ? (
            <Caption data-testid='harbour-log-blocked'>
              {HARBOUR_LOG_START_WORK_FIRST}
            </Caption>
          ) : null}

          <Field title={HARBOUR_TOOLTIPS.logOutcome}>
            <FieldLabel>Outcome</FieldLabel>
            <ChoiceRow role='radiogroup' data-testid='harbour-log-outcome'>
              {HARBOUR_OUTCOMES.map(key => (
                <Choice
                  key={key}
                  type='button'
                  data-testid={`harbour-log-outcome-${key}`}
                  data-selected={outcome === key ? 'true' : 'false'}
                  onClick={() => handleOutcomeChange(key)}
                >
                  {getHarbourOutcomeLabel(key)}
                </Choice>
              ))}
            </ChoiceRow>
            {outcome === HARBOUR_OUTCOME.CLOSED ? (
              <Caption data-testid='harbour-closed-log-copy'>
                {HARBOUR_CLOSED_LOG_COPY}
              </Caption>
            ) : null}
          </Field>

          <Field title={HARBOUR_TOOLTIPS.logIntent}>
            <FieldLabel>Intent (optional)</FieldLabel>
            <ChoiceRow role='group' data-testid='harbour-log-intent'>
              {HARBOUR_INTENTS.map(key => (
                <Choice
                  key={key}
                  type='button'
                  data-testid={`harbour-log-intent-${key}`}
                  data-selected={intent === key ? 'true' : 'false'}
                  onClick={() => setIntent(intent === key ? '' : key)}
                >
                  {getHarbourIntentLabel(key)}
                </Choice>
              ))}
            </ChoiceRow>
          </Field>

          <Field title={HARBOUR_TOOLTIPS.logFollowUp}>
            <FieldLabel
              htmlFor={showFollowUpDate ? 'harbour-log-follow-up' : undefined}
            >
              Follow-up date
            </FieldLabel>
            {showFollowUpDate ? (
              <DateInput
                id='harbour-log-follow-up'
                type='date'
                data-testid='harbour-log-follow-up'
                value={followUpDate}
                onChange={event => setFollowUpDate(event.target.value)}
              />
            ) : (
              <Caption data-testid='harbour-log-follow-up-hint'>
                {outcome === HARBOUR_OUTCOME.CLOSED
                  ? HARBOUR_LOG_FOLLOW_UP_CLOSED_COPY
                  : HARBOUR_LOG_FOLLOW_UP_WAIT_COPY}
              </Caption>
            )}
          </Field>

          <Field title={HARBOUR_TOOLTIPS.logNote}>
            <FieldLabel htmlFor='harbour-log-note'>Note (optional)</FieldLabel>
            <NoteInput
              id='harbour-log-note'
              data-testid='harbour-log-note'
              rows={3}
              value={note}
              onChange={event => setNote(event.target.value)}
            />
          </Field>

          <PrimaryButton
            type='button'
            disabled={!canSave}
            data-testid='harbour-log-save'
            onClick={handleSaveLog}
          >
            {saveBusy ? 'Saving…' : HARBOUR_LOG_SAVE_LABEL}
          </PrimaryButton>
        </Log>

        <HarbourDrawerOfframps
          venueId={venueId}
          venueName={briefing.venueName}
          column={briefing.column}
          alreadyOwned={alreadyOwned}
          alreadyTaken={alreadyTaken}
          outcome={outcome}
          lastOutcome={briefing.lastOutcome || savedLog?.outcome || ''}
          sisterGroup={sisterGroup}
          onWorkChanged={onWorkChanged}
          onDone={onClose}
        />
      </Panel>
    </Overlay>
  )
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px 16px;
  overflow: auto;
  background: rgba(18, 24, 18, 0.4);
`

const Panel = styled.aside`
  width: min(40rem, 100%);
  max-height: calc(100vh - 48px);
  overflow: auto;
  border: 1px solid #e6e8eb;
  border-radius: 8px;
  padding: 16px 18px 32px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 16px 48px rgba(18, 24, 18, 0.18);

  &[data-overdue='true'],
  &[data-forgotten='true'] {
    border-color: #e8c4c3;
    border-left: 4px solid #c45c5c;
    background: #fff6f5;
  }
`

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`

const TitleBlock = styled.div`
  min-width: 0;
`

const Title = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
`

const HeadAside = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: flex-start;
  gap: 8px;
`

const PlanBadge = styled.span`
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: #eef1f4;
  color: #555;

  &[data-plan='paid'] {
    background: #e8f3d8;
    color: #2d5a1e;
  }
`

const Close = styled.button`
  border: 0;
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const UrgencyRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`

const UrgentBadge = styled.span`
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  color: #7a2e2e;
  background: #f7e7e7;
`

const ContactBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const ContactRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 13px;
  line-height: 1.35;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
`

const ContactName = styled.span`
  font-weight: 600;
`

const ContactSep = styled.span`
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
  font-weight: 400;
`

const PhoneNumber = styled.span`
  font-variant-numeric: tabular-nums;
`

const NoPhoneRow = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #7a2e2e;
  background: #fdf6f6;
`

const CsOwner = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const EmailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;

  &[data-emphasized='true'] {
    padding: 6px 8px;
    border-radius: 6px;
    background: #f3f7fa;
    font-weight: 600;
  }
`

const EmailList = styled.div`
  min-width: 0;
  flex: 1 1 auto;
  overflow-wrap: anywhere;
`

const PhoneLink = styled.a`
  color: inherit;
`

const WhatsAppChatLink = styled.a`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
`

const Status = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const WhyHere = styled.p`
  margin: 0;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #d7e3dc;
  background: #f4f8f6;
  font-size: 13px;
  line-height: 1.4;
  color: #2f5638;
`

const Suggested = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
`

const Block = styled.section`
  display: flex;
  flex-direction: column;
  gap: 6px;

  &[data-variant='ollie'] {
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid #d7e3dc;
    background: #f4f8f6;
  }
`

const BlockLabel = styled.h5`
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
`

const BlockLabelWithIcon = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

const List = styled.ul`
  margin: 0;
  padding-left: 1.1rem;
  font-size: 13px;
  line-height: 1.45;
`

const Caption = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const ChipRowSecondary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  opacity: 0.72;
`

const Chip = styled.span`
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: 600;
  background: #f3f4f6;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};

  &[data-tier='secondary'] {
    font-size: 11px;
    font-weight: 500;
    background: #f4f4f4;
    color: #888;
  }

  &[data-tone='fresh'] {
    background: #e8f3d8;
    color: #2d5a1e;
  }

  &[data-tone='quiet'] {
    background: #f7f0e0;
    color: #7a5a1e;
  }

  &[data-tone='never'] {
    background: #f7e7e7;
    color: #7a2e2e;
    font-weight: 700;
  }

  &[data-tier='secondary'][data-tone='fresh'] {
    background: #eef3ea;
    color: #5a7a4a;
  }

  &[data-tier='secondary'][data-tone='quiet'] {
    background: #f5f2eb;
    color: #8a7a62;
  }

  &[data-tier='secondary'][data-tone='never'] {
    background: #f8f0f0;
    color: #9a6a6a;
    font-weight: 600;
  }
`

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`

const GhostButton = styled.button`
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0;
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
`

const SecondaryButton = styled.button`
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: #fff;
  border-radius: 6px;
  padding: 6px 10px;
  cursor: pointer;
  font-size: 13px;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`

const PrimaryButton = styled.button`
  border: 0;
  background: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
  color: #fff;
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 13px;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`

const Log = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 8px;
  border-top: 1px solid #e6e8eb;
`

const LastLog = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f4f6f0;
`

const LastLogLine = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
`

const LastLogNote = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
`

const ChoiceRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const Choice = styled.button`
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: #fff;
  border-radius: 6px;
  padding: 6px 10px;
  cursor: pointer;
  font-size: 13px;

  &[data-selected='true'] {
    border-color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
    background: #f4f6f0;
  }
`

const DateInput = styled.input`
  width: 12rem;
  padding: 6px 8px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  font-size: 13px;
`

const NoteInput = styled.textarea`
  width: 100%;
  max-width: 28rem;
  padding: 6px 8px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  font-size: 13px;
  font-family: inherit;
`

export default HarbourDrawer
